import type { AnalyticsProperties } from "./types";

export const WEB_CAMPAIGN_ATTRIBUTION_STORAGE_KEY = "meongnyang.web-campaign-attribution.v1";
export const WEB_CAMPAIGN_ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const UTM_FIELDS = [
  ["utm_source", "source"],
  ["utm_medium", "medium"],
  ["utm_campaign", "campaign"],
  ["utm_id", "id"],
  ["utm_content", "content"],
  ["utm_term", "term"],
] as const;

export type WebCampaignAttribution = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  id: string | null;
  content: string | null;
  term: string | null;
  landingPath: string;
  capturedAt: string;
};

type StorageLike = Pick<Storage, "getItem" | "setItem">;

let memoryFallback: WebCampaignAttribution | null = null;

function normalizeValue(value: string | null) {
  const normalized = value?.trim() ?? "";
  if (
    !normalized
    || normalized.length > 128
    // eslint-disable-next-line no-control-regex -- campaign values must not contain non-printing controls.
    || /[\u0000-\u001f\u007f<>#]/u.test(normalized)
    || normalized.includes("\uFFFD")
    || /(?:https?:|javascript:|data:)/iu.test(normalized)
  ) {
    return null;
  }
  return normalized;
}

function getSearchParams(search: string) {
  try {
    return new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  } catch {
    return new URLSearchParams();
  }
}

function getSingleSafeValue(params: URLSearchParams, queryName: string) {
  const values = params.getAll(queryName);
  return values.length === 1 ? normalizeValue(values[0] ?? null) : null;
}

export function parseWebCampaignAttribution(
  search: string,
  pathname: string,
  now = new Date(),
): WebCampaignAttribution | null {
  const params = getSearchParams(search);
  const values = Object.fromEntries(
    UTM_FIELDS.map(([queryName, fieldName]) => [fieldName, getSingleSafeValue(params, queryName)]),
  ) as Pick<WebCampaignAttribution, "source" | "medium" | "campaign" | "id" | "content" | "term">;

  if (!Object.values(values).some(Boolean)) {
    return null;
  }

  return {
    ...values,
    landingPath: pathname || "/",
    capturedAt: now.toISOString(),
  };
}

function normalizeStoredAttribution(value: unknown, now: Date): WebCampaignAttribution | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<WebCampaignAttribution>;
  if (typeof candidate.capturedAt !== "string" || typeof candidate.landingPath !== "string") return null;
  const capturedAt = Date.parse(candidate.capturedAt);
  if (!Number.isFinite(capturedAt) || capturedAt > now.getTime() || now.getTime() - capturedAt > WEB_CAMPAIGN_ATTRIBUTION_TTL_MS) {
    return null;
  }
  const fieldsAreValid = UTM_FIELDS.every(([, fieldName]) => {
    const field = candidate[fieldName];
    return field === null || (typeof field === "string" && normalizeValue(field) === field);
  });
  if (!fieldsAreValid || !candidate.landingPath.startsWith("/") || /[?#]/u.test(candidate.landingPath)) return null;
  const normalizedFields = Object.fromEntries(
    UTM_FIELDS.map(([, fieldName]) => [fieldName, candidate[fieldName] ?? null]),
  ) as Pick<WebCampaignAttribution, "source" | "medium" | "campaign" | "id" | "content" | "term">;
  if (!Object.values(normalizedFields).some(Boolean)) return null;
  return {
    ...normalizedFields,
    landingPath: candidate.landingPath,
    capturedAt: new Date(capturedAt).toISOString(),
  };
}

function getStorage(storage?: StorageLike | null) {
  if (storage !== undefined) return storage;
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function readWebCampaignAttribution(storage?: StorageLike | null, now = new Date()) {
  const targetStorage = getStorage(storage);
  if (targetStorage) {
    try {
      const raw = targetStorage.getItem(WEB_CAMPAIGN_ATTRIBUTION_STORAGE_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      const normalized = normalizeStoredAttribution(parsed, now);
      if (normalized) {
        memoryFallback = normalized;
        if (raw !== JSON.stringify(normalized)) {
          targetStorage.setItem(WEB_CAMPAIGN_ATTRIBUTION_STORAGE_KEY, JSON.stringify(normalized));
        }
        return normalized;
      }
      if (raw) targetStorage.setItem(WEB_CAMPAIGN_ATTRIBUTION_STORAGE_KEY, "");
    } catch {
      // Keep using the in-memory context when storage is unavailable or corrupt.
    }
  }
  return normalizeStoredAttribution(memoryFallback, now);
}

function sameTouch(left: WebCampaignAttribution | null, right: WebCampaignAttribution) {
  return Boolean(left && UTM_FIELDS.every(([, key]) => left[key] === right[key]));
}

export function captureWebCampaignAttribution(
  search: string,
  pathname: string,
  options: { storage?: StorageLike | null; now?: Date } = {},
) {
  const now = options.now ?? new Date();
  const current = readWebCampaignAttribution(options.storage, now);
  const incoming = parseWebCampaignAttribution(search, pathname, now);
  if (!incoming) return { attribution: current, captured: false };
  if (sameTouch(current, incoming)) return { attribution: current, captured: false };

  memoryFallback = incoming;
  const targetStorage = getStorage(options.storage);
  if (targetStorage) {
    try {
      targetStorage.setItem(WEB_CAMPAIGN_ATTRIBUTION_STORAGE_KEY, JSON.stringify(incoming));
    } catch {
      // Same-tab popup attribution still works through memoryFallback.
    }
  }
  return { attribution: incoming, captured: true };
}

export function sanitizeCampaignPagePath(pathname: string, search: string) {
  const params = getSearchParams(search);
  const safeParams = new URLSearchParams();
  for (const [queryName] of UTM_FIELDS) {
    const value = getSingleSafeValue(params, queryName);
    if (value) safeParams.set(queryName, value);
  }
  const query = safeParams.toString();
  return `${pathname || "/"}${query ? `?${query}` : ""}`;
}

export function sanitizeCampaignUrl(value: string, currentOrigin = "https://mypetroutine.petnow.io") {
  try {
    const url = new URL(value, currentOrigin);
    const path = sanitizeCampaignPagePath(url.pathname, url.search);
    return `${url.origin}${path}`;
  } catch {
    return sanitizeCampaignPagePath(value.split(/[?#]/u, 1)[0] || "/", "");
  }
}

export function webCampaignAttributionProperties(
  attribution: WebCampaignAttribution | null = readWebCampaignAttribution(),
): AnalyticsProperties {
  if (!attribution) {
    return {
      campaign_key: "web_unknown",
      campaign_source: null,
      campaign_medium: null,
      campaign_id: null,
      campaign_content: null,
      campaign_term: null,
      landing_path: null,
    };
  }
  return {
    campaign_key: attribution.campaign || attribution.id || "web_unknown",
    campaign_source: attribution.source,
    campaign_medium: attribution.medium,
    campaign_id: attribution.id,
    campaign_content: attribution.content,
    campaign_term: attribution.term,
    landing_path: attribution.landingPath,
  };
}

export function resetWebCampaignAttributionMemoryForTests() {
  memoryFallback = null;
}
